<template>
  <div>
    <CBreadcrumbs :breadcrumb="breadcrumb" />
    <TheHero
      v-if="hasHeroImage"
      :title="contentFields!.title"
      :description="contentFields!.ingress"
      :background-url-desktop="heroUrlDesktop"
      :background-url-mobile="heroUrlMobile"
      :icon-url="iconUrl"
      :overlay="contentFields!.overlay"
    />
    <CDropdown
      v-if="tags && tags.length"
      class="m-4 md:hidden"
      :options="mobileNavLinkData"
      :value="selectedTag || unTaggedLabel.toLowerCase()"
      :label="t('filter')"
      @update:model-value="changeRoute"
    />
    <div data-test="content-listing-page-body" class="flex w-full grow">
      <aside
        v-if="tags.length"
        class="w-1/5 min-w-[200px] bg-white hidden md:block"
        data-test="content-listing-page-nav-sidebar"
      >
        <CHeading :level="4" class="mt-6 mb-2 ml-6 text-primary-900">{{ t('filter') }}</CHeading>
        <CNavigationList :nav-links="getNavLinks()" />
      </aside>
      <div class="w-full p-4 md:px-10 lg:pt-10">
        <CHeading v-if="routeTitle" data-test="route-title" :level="3" class="mb-8 text-primary-900">
          {{ routeTitle }}
        </CHeading>
        <div
          v-if="taggedContentPages.length && !banners.length"
          class="grid grid-cols-1 gap-4 mx-auto max-w-[1900px] md:grid-cols-3"
        >
          <ContentPageTile v-for="(tile, index) in taggedContentPages" :key="index" :content="tile" />
        </div>
        <div
          v-if="banners.length"
          class="grid grid-cols-1 p-4 mx-auto my-0 max-w-[1900px] lg:gap-4 lg:grid-cols-3 md:px-10 lg:px-20 lg:pt-10"
        >
          <SubBanner v-for="banner in banners" :key="banner.linkUrl" :content="banner" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import gql from '@/gql/contentful';

// By including a wildcard route we get a generated route rule that triggers this bug: https://github.com/unjs/nitro/issues/1751
// defineRouteRules({
//   swr: 60 * 10,
//   cache: {
//     base: 'cache-storage',
//   },
// });

type Tag = {
  tag: string;
  name: string;
};

type ContentfulTag = {
  id: string;
  name: string;
};

type ContentfulImage = {
  url: string;
  width?: number;
  height?: number;
};

type ContentfulMetadata = {
  tags: ContentfulTag[];
};

type SubBanner = {
  title?: string;
  textRow1?: string;
  textRow2?: string;
  image?: ContentfulImage;
  icon?: ContentfulImage;
  link: string;
  isExternalLink: boolean;
};

type ContentfulLinkedFrom = {
  contentListingPageCollection: {
    items: { slug: string }[];
  };
};

type ContentfulRichText = {
  json: any;
  links: any;
};

type ContentPage = {
  slug: string;
  title: string;
  preTitle: string;
  contentfulMetadata: ContentfulMetadata;
  tagOrder: string[];
  banner?: SubBanner | null;
  thumbnail: ContentfulImage;
  ingress: ContentfulRichText;
  linkedFrom: ContentfulLinkedFrom;
};

type ContentListingPage = {
  heroDesktop: ContentfulImage;
  heroMobile: ContentfulImage;
  icon: ContentfulImage;
  title: string;
  ingress: string;
  overlay: boolean;
  metaDescription: string;
  contentfulMetadata: ContentfulMetadata;
  contentCollection: {
    items: ContentPage[];
  };
  tagOrder: string[];
};

type ContentListingPageCollection = {
  contentListingPageCollection: {
    items: ContentListingPage[];
  };
};

const route = useRoute();
const { fetchEntryWithGraphQL, fetchTags } = useContentful();

const contentFields = ref<ContentListingPage | null>(null);
const contentPages = ref<ContentPage[]>([]);
const tags = ref<Tag[]>([]);
const tagOrder = ref<string[]>([]);
const selectedTag = ref('');
const haveUntagged = ref(false);
const defaultTag = ref('');
const { t } = useI18n();
const { public: config } = useRuntimeConfig();

const slug = route.path.replace(/\//g, '');
const tag = route.query?.tag as string;

const { data } = await useAsyncData(`contentListingPage-${slug}`, async () => {
  const { data, error } = await fetchEntryWithGraphQL<ContentListingPageCollection>(gql.getContentListingPage, {
    slug,
  });

  if (!data && error) {
    // Throw 404 when we get an error, better than a 500
    throw createError({ statusCode: 404 });
  } else if (data && !data.contentListingPageCollection.items.length) {
    // If we have get data but no items we throw a 404
    throw createError({ statusCode: 404 });
  }

  // ToDo: Move this to the store when campaigns is updated to use GraphQL
  const listingPageEntry = data!.contentListingPageCollection.items[0] as ContentListingPage;

  const currentContentPages: ContentPage[] = listingPageEntry.contentCollection.items.filter(Boolean);

  const currentTagOrder = listingPageEntry.tagOrder || [];
  let currentHaveUntagged = false;

  const filteredTags: string[] = Array.from(
    new Set(
      currentContentPages.reduce((res: string[], item) => {
        if (!currentHaveUntagged && item.contentfulMetadata && !item.contentfulMetadata?.tags?.length)
          currentHaveUntagged = true;

        const tags = item.contentfulMetadata?.tags.length ? item.contentfulMetadata.tags.map((tag) => tag.id) : [];

        return res.concat(tags);
      }, []),
    ),
  );

  const { data: currentTags, error: tagsError } = await fetchTags();
  if (tagsError) {
    throw createError({ statusCode: tagsError.statusCode, message: tagsError.message as string });
  }

  const mappedTags = filteredTags.map((t) => {
    const foundTag = currentTags?.items.find((tag) => tag.sys.id === t);
    return {
      name: foundTag?.name,
      tag: t,
    };
  }) as Tag[];

  const mappedTagOrder = currentTagOrder
    .map((t: string) => mappedTags.find((mt) => t.toLowerCase() === mt.name.toLowerCase()))
    .filter(Boolean) as Tag[];

  let startTag;
  if (mappedTagOrder.length) {
    startTag = mappedTagOrder.find((t) => filteredTags.includes(t?.tag));
  }

  return {
    listingPageEntry,
    startTag,
    filteredTags,
    mappedTagOrder,
    currentTagOrder,
    currentContentPages,
    currentHaveUntagged,
  };
});

if (!data || !data.value) {
  throw createError({ statusCode: 404 });
}

const {
  listingPageEntry,
  startTag,
  filteredTags,
  mappedTagOrder,
  currentTagOrder,
  currentContentPages,
  currentHaveUntagged,
} = data.value;

provide('contentListingPage', ref(listingPageEntry));

if (!tag || !filteredTags.includes(tag)) {
  if (startTag) {
    navigateTo(`${route.path}?tag=${startTag.tag}`);
  } else if (filteredTags.length) {
    const newTag = filteredTags[0];
    navigateTo(`${route.path}?tag=${newTag}`);
  }
}

contentFields.value = listingPageEntry;
tags.value = mappedTagOrder;
selectedTag.value = tag;
defaultTag.value = startTag?.tag || tag;
tagOrder.value = currentTagOrder;
contentPages.value = currentContentPages;
haveUntagged.value = currentHaveUntagged;

const unTaggedLabel = t('uncategorized');

const getNavLinks = () => {
  const tagsToNavLinks = tags.value.map((tag) => ({
    name: tag.tag,
    to: `?tag=${tag.tag}`,
    text: tag.name[0].toUpperCase() + tag.name.slice(1),
  }));

  if (haveUntagged.value) {
    tagsToNavLinks.push({
      name: unTaggedLabel.toLowerCase(),
      to: '?tag=',
      text: unTaggedLabel,
    });
  }

  if (tagOrder.value.length) {
    tagsToNavLinks.sort((first, second) => {
      let firstPosition = tagOrder.value.indexOf(first.text);
      let secondPosition = tagOrder.value.indexOf(second.text);

      firstPosition = firstPosition === -1 ? Number.MAX_SAFE_INTEGER : firstPosition;
      secondPosition = secondPosition === -1 ? Number.MAX_SAFE_INTEGER : secondPosition;

      return firstPosition - secondPosition;
    });
  }

  return tagsToNavLinks;
};

const changeRoute = (to: any) => {
  if (to === unTaggedLabel.toLowerCase()) {
    navigateTo({ path: '?tag=' });
  } else {
    navigateTo({ path: `?tag=${to}` });
  }
};

const heroUrlDesktop = computed(() => {
  return contentFields.value?.heroDesktop?.url;
});

const heroUrlMobile = computed(() => {
  return contentFields.value?.heroMobile?.url;
});

const hasHeroImage = computed(() => {
  return !!(heroUrlDesktop.value && heroUrlMobile.value);
});

const iconUrl = computed(() => {
  return contentFields.value?.icon?.url;
});

const breadcrumb = computed(() => {
  return {
    name: contentFields.value?.title,
    parents: [],
  };
});
const taggedContentPages = computed(() => {
  if (!tags.value.length) {
    return contentPages.value || [];
  }

  if (!selectedTag.value) {
    // When selectedTag is empty take entries without tags
    return contentPages.value.filter((item) => !item?.contentfulMetadata?.tags.length) || [];
  }

  return (
    contentPages.value.filter(
      (item) => item.contentfulMetadata?.tags.filter((tagObject) => tagObject?.id === selectedTag.value).length >= 1,
    ) || []
  );
});

const banners = computed(() => {
  return taggedContentPages.value.map((item) => item.banner).filter(Boolean) || [];
});

const mobileNavLinkData = computed(() => {
  return getNavLinks().map((l) => ({ label: l.text, value: l.name }));
});
const routeTitle = computed(() => {
  if (!tags.value.length) return '';

  if (selectedTag.value?.length) {
    const tag = tags.value.find((tag) => selectedTag.value === tag.tag);

    if (tag) {
      return tag.name[0].toUpperCase() + tag.name.slice(1);
    }
  }

  return unTaggedLabel;
});

const hasTags = computed(() => {
  return tags.value && tags.value.length;
});

onMounted(() => {
  const tag = route.query?.tag as string;

  if (!tag || !tags.value.some((obj) => obj.tag === tag)) {
    const newTag = tagOrder.value.find((t) => tags.value.some((obj) => obj.name === tag));
    if (newTag) {
      navigateTo({ path: route.path, query: { tag: newTag } });
    }
  }
});

onBeforeRouteUpdate((to, from, next) => {
  if (to.query?.tag === selectedTag.value) {
    next();
    return;
  }

  if (to.path === from.path && !to.query?.tag && hasTags.value) {
    selectedTag.value = defaultTag.value;
    next(`${to.path}?tag=${selectedTag.value}`);
    return;
  }

  next();
  selectedTag.value = to.query?.tag as string;
});

useHead(() => ({
  title: contentFields.value?.title,
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: contentFields.value?.metaDescription,
    },
  ],
  link: [{ rel: 'canonical', href: `${config.baseUrl}${route.path}` }],
}));
</script>

<script lang="ts">
export default {
  name: 'ContentListingPage',
};
</script>
